const Util = {
  /**
   * Read cookie by name
   * @param name
   */
  getCookie(name: string): string | null {
    const nameLenPlus = name.length + 1;
    return (
      document.cookie
        .split(';')
        .map((c) => c.trim())
        .filter((cookie) => cookie.substring(0, nameLenPlus) === `${name}=`)
        .map((cookie) => decodeURIComponent(cookie.substring(nameLenPlus)))[0] || null
    );
  },

  /**
   * Function to set cookies. This is used in Dev mode only
   * @param name
   * @param val
   */
  setCookie(name: string, val: string | null) {
    const value = val;
    document.cookie = `${name}=${value}; path=/; secure;`;
  },

  /**
   * Is the app running in development mode
   */
  isDevelopmentMode(): boolean {
    return !process.env.NODE_ENV || process.env.NODE_ENV === 'development';
  },

  /**
   * Function to get backend host url.
   * This is based on environment variable if the app is
   * running in development mode,.ie, 'npm start'
   * If not running in dev mode, it returns the current host url based on window.location
   */
  getBackendHost(): string | undefined {
    return this.isDevelopmentMode()
      ? process.env.REACT_APP_DEV_PROXY_BACKEND
      : this.getCurrentHost();
  },

  getAWSHost(): string | undefined {
    if (this.isDevelopmentMode()) return process.env.REACT_APP_DEV_AI_PROXY_BACKEND;
    if (this.isQAEnv()) return process.env.REACT_APP_DEV_AI_PROXY_TO_QA;
    if (this.isBaiQAEnv()) return process.env.REACT_APP_DEV_AI_PROXY_TO_BAI;
    if (this.isPRODEnv()) return process.env.REACT_APP_DEV_AI_PROXY_TO_PLATFORM;
    if (this.isProdCloneEnv()) return process.env.REACT_APP_DEV_AI_PROXY_TO_PROD_CLONE;
    if (this.isBiAIAgentEnv()) {
      return process.env.REACT_APP_DEV_AI_PROXY_TO_BIAIAGENTP_AGENT_BUILDER;
    }
    if (this.isLazarusEnv()) {
      return process.env.REACT_APP_DEV_AI_PROXY_TO_LAZARUS_AGENT_BUILDER;
    }
    if (this.isProdClone4Env()) return process.env.REACT_APP_DEV_AI_PROXY_TO_PREPROD;
    if (this.isPopeyeEnv()) return process.env.REACT_APP_DEV_AI_PROXY_TO_POPEYE;
    if (this.isEuQaEnv()) return process.env.REACT_APP_DEV_AI_PROXY_TO_EU_QA;
    if (this.isEuPRODEnv()) return process.env.REACT_APP_DEV_AI_PROXY_TO_EU_PROD;
    // if none of the above env, then return PROD End Point
    return process.env.REACT_APP_DEV_AI_PROXY_TO_BAI;
  },
  getEndpointUrlHost(endpointUrl: string): string | undefined {
    if (this.isDevelopmentMode()) return `${process.env.REACT_APP_DEV_PROXY_AGENT_BUILDER}${endpointUrl}`;
    return endpointUrl;
  },
  getAWSHostForAgentFoundation(): string | undefined {
    const BASE_PATH = '/api/v1';
    if (this.isDevelopmentMode()) return `${process.env.REACT_APP_DEV_PROXY_AGENT_BUILDER}${process.env.REACT_APP_DEV_AGENT_BUILDER_SANDBOX}${BASE_PATH}`;
    if (this.isBiAIAgentEnv()) return `${process.env.REACT_APP_DEV_AI_PROXY_TO_BIAIAGENTP_AGENT_BUILDER}${BASE_PATH}`;
    if (this.isLazarusEnv()) return `${process.env.REACT_APP_DEV_AI_PROXY_TO_LAZARUS_AGENT_BUILDER}${BASE_PATH}`;
    if (this.isQAEnv()) return `${process.env.REACT_APP_DEV_AI_PROXY_TO_QA_AGENT_BUILDER}${BASE_PATH}`;
    if (this.isProdCloneEnv()) return `${process.env.REACT_APP_DEV_AI_PROXY_TO_PRODCLONE_AGENT_BUILDER}${BASE_PATH}`;
    if (this.isProdClone4Env()) return `${process.env.REACT_APP_DEV_AI_PROXY_TO_PREPROD_AGENT_BUILDER}${BASE_PATH}`;
    if (this.isPRODEnv()) return `${process.env.REACT_APP_DEV_AI_PROXY_TO_PROD_AGENT_BUILDER}${BASE_PATH}`;
    if (this.isPopeyeEnv()) return `${process.env.REACT_APP_DEV_AI_PROXY_TO_POPEYE_AGENT_BUILDER}${BASE_PATH}`;
    if (this.isEuQaEnv()) return `${process.env.REACT_APP_DEV_AI_PROXY_TO_EU_QA_AGENT_BUILDER}${BASE_PATH}`;
    if (this.isEuPRODEnv()) return `${process.env.REACT_APP_DEV_AI_PROXY_TO_EU_PROD_AGENT_BUILDER}${BASE_PATH}`;
    return `${process.env.REACT_APP_DEV_AI_PROXY_TO_BIAIAGENTP_AGENT_BUILDER}${BASE_PATH}`;
  },
  getAWSHostForAgentFoundationOAuth(): string | undefined {
    if (this.isDevelopmentMode()) return process.env.REACT_APP_DEV_AGENT_BUILDER_SANDBOX;
    if (this.isBiAIAgentEnv()) {
      return process.env.REACT_APP_DEV_AI_PROXY_TO_BIAIAGENTP_AGENT_BUILDER;
    }
    if (this.isLazarusEnv()) {
      return process.env.REACT_APP_DEV_AI_PROXY_TO_LAZARUS_AGENT_BUILDER;
    }
    if (this.isBaiQAEnv()) {
      return process.env.REACT_APP_DEV_AI_PROXY_TO_BAIQA1_AGENT_BUILDER;
    }
    if (this.isQAEnv()) return process.env.REACT_APP_DEV_AI_PROXY_TO_QA_AGENT_BUILDER;
    if (this.isProdCloneEnv()) return process.env.REACT_APP_DEV_AI_PROXY_TO_PRODCLONE_AGENT_BUILDER;
    if (this.isProdClone4Env()) return process.env.REACT_APP_DEV_AI_PROXY_TO_PREPROD_AGENT_BUILDER;
    if (this.isPRODEnv()) return process.env.REACT_APP_DEV_AI_PROXY_TO_PROD_AGENT_BUILDER;
    if (this.isPopeyeEnv()) return process.env.REACT_APP_DEV_AI_PROXY_TO_POPEYE_AGENT_BUILDER;
    if (this.isEuQaEnv()) return process.env.REACT_APP_DEV_AI_PROXY_TO_EU_QA_AGENT_BUILDER;
    if (this.isEuPRODEnv()) return process.env.REACT_APP_DEV_AI_PROXY_TO_EU_PROD_AGENT_BUILDER;
    return process.env.REACT_APP_DEV_AI_PROXY_TO_BIAIAGENTP_AGENT_BUILDER;
  },
  getMcpRegistryHost(): string {
    const QA_REGISTRY = process.env.REACT_APP_MCP_REGISTRY_QA || 'https://mcp-registry.qa.apiida-pipeline.boomi.com';
    if (this.isDevelopmentMode()) return `http://localhost:8012/${QA_REGISTRY}`;
    if (this.isQAEnv()) return QA_REGISTRY;
    if (this.isProdCloneEnv()) return process.env.REACT_APP_MCP_REGISTRY_PROD_CLONE || QA_REGISTRY;
    if (this.isProdClone4Env()) return process.env.REACT_APP_MCP_REGISTRY_PREPROD || QA_REGISTRY;
    if (this.isPRODEnv()) return process.env.REACT_APP_MCP_REGISTRY_PROD || QA_REGISTRY;
    if (this.isEuQaEnv()) return process.env.REACT_APP_MCP_REGISTRY_EU_QA || QA_REGISTRY;
    if (this.isEuPRODEnv()) return process.env.REACT_APP_MCP_REGISTRY_EU_PROD || QA_REGISTRY;
    return QA_REGISTRY;
  },
  /**
   * Function to get current host url'
   */
  getCurrentHost(): string {
    return `${window.location.protocol}//${window.location.host}`;
  },

  /**
   Checking for Env's since every env has different endpoint
   */
  isQAEnv(): boolean {
    return ['qa.boomi.com'].includes(window.location.hostname);
  },
  isPRODEnv(): boolean {
    return ['platform.boomi.com'].includes(window.location.hostname);
  },
  isBaiQAEnv(): boolean {
    return ['baiqa1.sandbox.boomi.com'].includes(window.location.hostname);
  },
  isProdCloneEnv(): boolean {
    return ['prodclone.boomi.com'].includes(window.location.hostname);
  },
  isBiAIAgentEnv(): boolean {
    return ['biaiagentp.datalake-sandbox.boomi.com'].includes(window.location.hostname);
  },
  isLazarusEnv(): boolean {
    return ['lazarus.ras-sandbox.boomi.com'].includes(window.location.hostname);
  },
  isProdClone4Env(): boolean {
    return ['pc4.preprod.boomi.com'].includes(window.location.hostname);
  },
  isPopeyeEnv(): boolean {
    return ['popeye1.sandbox.boomi.com'].includes(window.location.hostname);
  },
  isEuQaEnv(): boolean {
    return ['euqapl.eu-pipeline.boomi.com'].includes(window.location.hostname);
  },
  isEuPRODEnv(): boolean {
    return ['platform.gb.boomi.com'].includes(window.location.hostname);
  },
  // End checking env's for fetching endpoint
};

export default Util;
